import { createAsyncThunk } from "@reduxjs/toolkit"

import i18n from "../../../i18n.ts"
import { errorSave } from "../../../redux/errors.slice"
import { type BasePagination } from "../../../types/api.ts"
import { showResponseMessage } from "../../../utils/handlers/show-response-message"
import * as services from "../api/attachment.services"
import type { AttachmentListParams } from "../api/services.types.ts"
import type {
  Attachment,
  AttachmentFilterOptions
} from "../types/attachment.types.ts"
import { albumSliceName } from "./album.constants.ts"

// GET /project-management/attachments/
export const getAttachments = createAsyncThunk<
  Attachment[],
  AttachmentListParams | void
>(
  `${albumSliceName}/getAttachments`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getAttachments(params || undefined)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data as Attachment[]
  }
)

// GET /project-management/attachments/ (load more)
export const getMoreAttachments = createAsyncThunk<
  BasePagination<Attachment>,
  AttachmentListParams
>(
  `${albumSliceName}/getMoreAttachments`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getAttachments(params || undefined)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data as BasePagination<Attachment>
  }
)

// GET /project-management/attachments/filter_options/
export const getAttachmentsFilterOptions =
  createAsyncThunk<AttachmentFilterOptions>(
    `${albumSliceName}/getAttachmentsFilterOptions`,
    async (_, { dispatch, rejectWithValue }) => {
      const response = await services.getAttachmentsFilterOptions()

      if (!response.ok) {
        dispatch(errorSave(response?.data?.details))
        return rejectWithValue(response.data)
      }

      return response.data as AttachmentFilterOptions
    }
  )

// PATCH /project-management/attachments/{id}/
export const renameAttachment = createAsyncThunk<
  Attachment,
  { id: string; original_filename: string }
>(
  `${albumSliceName}/renameAttachment`,
  async ({ id, original_filename }, { dispatch, rejectWithValue }) => {
    const response = await services.renameAttachment(id, { original_filename })

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, i18n.t("projectFiles.rename.success"))

    return response.data?.details?.data as Attachment
  }
)
